import { useEffect, useState } from 'react';

import { getConfig, sendNotification } from 'utils/helperMethods';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import _ from 'lodash';
import { bumpReportExportTableParser } from 'parser/bumpReportExportTableParser';
import { ConfigKeys } from 'utils/Enums';

const errorMessage: string = 'Something went wrong!';

interface Props {
    url: string;
    method: 'post' | 'get' | 'put' | 'delete';
    queryKey: any[];
    onError?: Function;
    variables?: any;
    appConfig?: any;
    extras?: any;
    queryConfig?: {
        enabled?: boolean;
    };
}

interface outputType {
    data: any;
    loading: boolean;
    error: any;
    tableData: any;
}

function useFetchGraphExportRQ({
    url,
    method,
    onError,
    queryKey,
    variables = {},
    appConfig = {},
    queryConfig = {},
    extras = {},
}: Props): outputType {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<null | string>(null);
    const [data, setData] = useState<any | null>(null);
    const [tableData, setTableData] = useState<any>(null);
    const { decimalForNonPercentage } = getConfig(ConfigKeys.COMMON_CONFIG, appConfig);

    // Queries
    const {
        data: apiData,
        isFetching,
        isSuccess,
        fetchStatus,
        error: apiError,
    } = useQuery({
        queryKey,
        queryFn: async () => {
            //initializing states
            setData(null);
            setError(null);
            setLoading(true);

            try {
                const data = await axios({
                    method,
                    url,
                    data: { ...variables },
                });
                return data;
            } catch (error: any) {
                onFetchError(error?.message);
                return { data: { error: error?.message } };
            }
        },
        ...queryConfig,
        //  enabled: false,
    });

    const apiLoading = isFetching || fetchStatus === 'paused';
    const apiSuccess = !apiLoading && !apiError && isSuccess;

    //error handling
    if (!apiLoading && apiError && !error) {
        if (onError) return onError(apiError);
        onFetchError(apiError);
    }
    //error handling when fetching from cache
    if (apiData?.data && 'error' in apiData?.data && !error) {
        onFetchError(apiData?.data?.error);
        sendNotification('error', 'Failed to load Export data!');
    }

    useEffect(() => {
        //success handling
        if (apiSuccess && apiData?.data && queryConfig.enabled) {
            if (error) setError(null);
            onFetchComplete(apiData?.data);
        }
    }, [apiData?.data, apiSuccess, queryConfig.enabled]);

    //called when fetching is complete
    function onFetchComplete(payload: any) {
        try {
            if ('data' in payload && payload?.data?.length > 0) {
                const fieldKeyLabelMapping = appConfig?.bumpReport?.fieldKeyLabelMapping || {};
                const bumpChartY2AxisOptions = appConfig?.bumpReport?.bumpChartY2AxisOptions || {};
                const y2Metric = bumpChartY2AxisOptions?.[extras?.volTile?.key];
                const exportData: any[][] = payload?.data;
                const [headers, ...mainData] = exportData;
                const formattedHeader: string[] = [];
                if (headers && Array.isArray(headers)) {
                    headers.forEach((header: string) => {
                        if (header in fieldKeyLabelMapping) {
                            formattedHeader.push(fieldKeyLabelMapping[header]?.label);
                        } else if (
                            y2Metric &&
                            Array.isArray(y2Metric) &&
                            y2Metric.find((item) => item?.value === header)
                        ) {
                            formattedHeader.push(y2Metric.find((item) => item?.value === header)?.label);
                        } else {
                            formattedHeader.push(_.startCase(header));
                        }
                    });
                }
                const { columns, rowsData } = bumpReportExportTableParser(
                    formattedHeader,
                    mainData,
                    decimalForNonPercentage,
                );
                setTableData({ columns, rowsData });
                setData({ header: formattedHeader, data: mainData });
            }
        } catch (error) {
            console.error(errorMessage, error);
            setLoading(false);
        } finally {
            setLoading(false);
        }
    }

    //called when API throws an error
    function onFetchError(error: any = errorMessage) {
        setData(null);
        setLoading(false);
        setError(error);
    }
    const outputData: outputType = {
        loading,
        error,
        data,
        tableData,
    };
    return outputData;
}

export { useFetchGraphExportRQ };
